import type { UILabeledAggregation } from '../services/SiteReportingService/types';

export interface ServiceLevelReportSummaryDetail {
  withinSLA: number;
  smallSLAMiss: number;
  mediumSLAMiss: number;
  largeSLAMiss: number;
  total: number;
}

export interface ServiceLevelReportLineData extends ServiceLevelReportSummaryDetail {
  segmentStartTime: number;
}

export const serviceLevelReportSummary = (data: UILabeledAggregation[], slaDays = 0) => {
  const grouped = data.reduce<ServiceLevelReportSummaryDetail>(
    (accum, current) => {
      const days = Math.max(0, parseInt(current.label, 10));
      if (days <= slaDays) {
        accum.withinSLA += current.value;
      } else if (days === slaDays + 1) {
        accum.smallSLAMiss += current.value;
      } else if (days === slaDays + 2) {
        accum.mediumSLAMiss += current.value;
      } else if (days > slaDays + 2) {
        accum.largeSLAMiss += current.value;
      }
      accum.total += current.value;
      return accum;
    },
    { withinSLA: 0, smallSLAMiss: 0, mediumSLAMiss: 0, largeSLAMiss: 0, total: 0 },
  );

  return grouped;
};

export const serviceLevelLineData = (data: UILabeledAggregation[], slaDays = 0) => {
  const groupedByStartTime = data.reduce((accum: Record<string, any>, current) => {
    // aggregate by the number of days each bucket missed their SLA, normalize faster than expected results to 0
    const serviceLevelMissDays = Math.max(0, parseInt(current.label, 10));

    if (!accum[current.segmentStartTime]) {
      accum[current.segmentStartTime] = {
        withinSLA: 0,
        smallSLAMiss: 0,
        mediumSLAMiss: 0,
        largeSLAMiss: 0,
        total: 0,
      };
    }
    if (serviceLevelMissDays <= slaDays) {
      accum[current.segmentStartTime].withinSLA += current.value;
    } else if (serviceLevelMissDays === slaDays + 1) {
      accum[current.segmentStartTime].smallSLAMiss += current.value;
    } else if (serviceLevelMissDays === slaDays + 2) {
      accum[current.segmentStartTime].mediumSLAMiss += current.value;
    } else if (serviceLevelMissDays > slaDays + 2) {
      accum[current.segmentStartTime].largeSLAMiss += current.value;
    }
    accum[current.segmentStartTime].total += current.value;
    return accum;
  }, {});
  const lineData: ServiceLevelReportLineData[] = Object.keys(groupedByStartTime).map((key) => {
    const roundedPercentage = (amount: number, total: number) => {
      return Math.round(((amount * 100) / total + Number.EPSILON) * 100) / 100;
    };
    return {
      segmentStartTime: new Date(key).getTime(),
      withinSLA: roundedPercentage(groupedByStartTime[key].withinSLA, groupedByStartTime[key].total),
      smallSLAMiss: roundedPercentage(groupedByStartTime[key].smallSLAMiss, groupedByStartTime[key].total),
      mediumSLAMiss: roundedPercentage(groupedByStartTime[key].mediumSLAMiss, groupedByStartTime[key].total),
      largeSLAMiss: roundedPercentage(groupedByStartTime[key].largeSLAMiss, groupedByStartTime[key].total),
    } as ServiceLevelReportLineData;
  });
  return lineData;
};
